import { Core } from "@faire/design-tokens";
import {
  INPUT_HEIGHT,
  inputStyles,
  InputWrapper,
  inputBorderStyles,
} from "@faire/web/slate/Input";
import { Row } from "@faire/web/slate/Layout";
import { Span, TypographyStyles } from "@faire/web/slate/Typography";
import {
  highVisSlateComponent,
  HIGH_VIS_FILTER,
} from "@faire/web/slate/utils/highVisSlateComponent";
import styled, { css } from "styled-components";

import { DROPDOWN_LIST_HEIGHT } from "./constants";

export const MobileChevronWrapper = styled.div`
  position: absolute;
  pointer-events: none;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
`;

export const Wrapper = styled.div<{
  $disabled?: boolean;
  $disableInput?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
  ${InputWrapper} {
    cursor: pointer;
  }
  input {
    ${(props) =>
      props.$disableInput && !props.$disabled
        ? css`
            color: transparent;
            text-shadow: 0 0 0 ${Core.surface.default};
          `
        : ""};
  }
  ${highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT)}
`;

export const MobileSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const MobileInnerWrapper = styled.div<{
  $showErrorState?: boolean;
  $disabled?: boolean;
  $borderless?: boolean;
}>`
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  width: 100%;
  min-height: 42px;
  ${inputStyles};
  ${({ $borderless }) => ($borderless ? "none" : inputBorderStyles)};
  padding: 0;
  background-color: ${({ $disabled }) =>
    $disabled ? Core.surface.subdued : Core.surface.inverse};
`;

export const StyledSelect = styled.select`
  ${TypographyStyles({ as: undefined })};
  display: flex;
  align-items: center;
  appearance: none;
  border-radius: 0;
  width: 100%;
  padding-right: 35px;
  padding-left: 16px;
  border: none;
  appearance: none;
  outline: none;
  /* transparent background-color is necessary to override some browsers attached stylesheets */
  background-color: transparent;
`;

export const DropdownWrapper = styled.div<{
  $showErrorMessage: boolean;
  $hasHelper: boolean;
  $isOpen: boolean;
  $isUp: boolean;
  $isPortal?: boolean;
}>`
  display: block;
  width: 100%;
  height: auto;
  max-height: ${DROPDOWN_LIST_HEIGHT}px;
  background-color: ${Core.surface.inverse};
  border: solid 1px ${Core.border.muted};
  position: absolute;
  overflow: auto;
  z-index: 2;
  box-sizing: border-box;
  // When not using a portal, position dropdown with CSS. Otherwise, Floating UI handles positioning.
  ${({ $isPortal = false, $isUp, theme, $showErrorMessage, $hasHelper }) => {
    if ($isPortal) {
      return "";
    }
    return css`
      ${$isUp ? "border-bottom: 0;" : "border-top: 0;"}
      bottom: ${$showErrorMessage || $hasHelper
        ? theme?.fonts?.sansSerif?.p?.lineHeight
        : "0"};
      transform: translate(0, ${$isUp ? css`-${INPUT_HEIGHT + 2}px` : "100%"});
    `;
  }}
`;

export const OptionRow = styled(Row)<{ $highlight?: boolean }>`
  min-height: 44px;
  cursor: pointer;

  border: 0;
  user-select: none;
  background: transparent;
  ${({ $highlight: highlight }) =>
    highlight
      ? css`
          background-color: ${Core.surface.subdued};
        `
      : ""}
`;

export const DropdownItem = styled.button<{
  $highlight?: boolean;
  $center?: boolean;
  $isCustom?: boolean;
}>`
  min-height: 44px;
  border: 0;
  user-select: none;
  background: transparent;
  width: 100%;
  display: block;
  cursor: pointer;

  ${({ theme }) =>
    TypographyStyles({
      color: theme.input.label.color ?? Core.text.default,
    })}
  text-align: left;
  padding: 12px 15px;

  ${({ $highlight }) =>
    $highlight
      ? css`
          background-color: ${Core.surface.subdued};
        `
      : ""};

  &:disabled {
    color: ${({ theme }) => theme.text.disabled};
    background-color: ${Core.surface.muted};
  }
`;

export const EditOptionButton = styled.button<{ $highlight?: boolean }>`
  ${({ theme }) =>
    TypographyStyles({ color: theme.input.label.color ?? Core.text.default })};
  padding: 12px 15px;

  ${({ $highlight }) =>
    $highlight
      ? css`
          background-color: ${Core.surface.subdued};
        `
      : ""}
`;

export const CustomEditSpan = styled(Span)`
  text-decoration: underline;
  &:hover {
    color: ${Core.text.enabled};
  }
`;

export const CreateNewOptionDropdownItem = styled(DropdownItem)`
  align-items: center;
  border-top: 1px solid ${Core.border.muted};
  padding: 12px 16px;
`;

export const CloseWrapper = styled.button<{
  $hasHelperText?: boolean;
}>`
  font-size: 16px;
  display: flex;
  width: 42px;
  height: 42px;
  left: calc(100% - 42px);
  position: absolute;
  bottom: ${({ $hasHelperText }) => ($hasHelperText ? 20 : 0)}px;
  align-items: center;
  justify-content: center;
`;
